import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, XCircle, RefreshCw, CheckCircle2, Clock, Mail, Smartphone, ArrowLeft, RotateCcw, Trophy, Ticket, CalendarClock, Building2, Camera, QrCode, FileDown } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { api, formatDateTime } from "../lib/api";
import { basarOrderQrUrl, basarOrderReceiptUrl, basarOrderStatusMeta, basarPaymentLabel, basarUrl, nok, shortTime } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100; // ~5 min

// Ordreside for loddkjøp: /basar/ordre/:orderId – Vipps-polling medan ordren ventar, loddnummer på skjermen når betalt, gevinstar etter trekning.
export default function BasarOrderPage() {
  const { orderId } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const pollCount = useRef(0);
  const stopped = useRef(false);

  const loadOrder = () => api.get(`/basar/orders/${orderId}`).then((res) => setData(res.data));

  useEffect(() => {
    loadOrder().catch((err) => {
      if (err?.response?.status === 404) setNotFound(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const pending = data?.order && ["pending_payment", "processing"].includes(data.order.status);

  useEffect(() => {
    if (!pending) return undefined;
    stopped.current = false;
    pollCount.current = 0;
    const poll = async () => {
      if (stopped.current) return;
      try {
        const { data: st } = await api.get(`/basar/orders/${orderId}/payment-status`);
        if (stopped.current) return;
        if (st.status !== "pending") {
          stopped.current = true;
          await loadOrder();
          return;
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setNotFound(true);
          stopped.current = true;
          return;
        }
      }
      pollCount.current += 1;
      if (pollCount.current >= MAX_POLLS) {
        setTimedOut(true);
        stopped.current = true;
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      stopped.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, orderId]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-24 text-center space-y-4" data-testid="basar-order-not-found">
          <XCircle className="w-10 h-10 mx-auto text-destructive" />
          <h1 className="text-xl font-heading font-semibold">{t("Fant ikke bestillingen")}</h1>
          <p className="text-sm text-muted-foreground">{t("Sjekk at lenken er riktig.")}</p>
          <Button asChild className="mt-2" data-testid="basar-order-home-button"><Link to="/basar"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Alle basarer")}</Link></Button>
        </div>
      </PublicLayout>
    );
  }

  if (!data) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-10 space-y-4" data-testid="basar-order-loading"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>
      </PublicLayout>
    );
  }

  const { order, email_enabled: emailEnabled } = data;
  const st = basarOrderStatusMeta(order);
  const numbers = order.numbers || [];
  const won = order.won || [];
  const paid = order.status === "paid";

  const statusBlock = () => {
    if (pending && timedOut) {
      return (
        <div className="text-center space-y-3" data-testid="basar-order-timeout">
          <RefreshCw className="w-10 h-10 mx-auto text-muted-foreground" />
          <h2 className="text-lg font-heading font-semibold">{t("Vi venter fortsatt på Vipps")}</h2>
          <p className="text-sm text-muted-foreground">{t("Vi har ikke fått bekreftelse enda. Hvis du har betalt, prøv å oppdatere siden.")}</p>
          <Button onClick={() => window.location.reload()} data-testid="basar-order-retry-button"><RefreshCw className="w-4 h-4 mr-1.5" />{t("Sjekk på nytt")}</Button>
        </div>
      );
    }
    if (pending) {
      return (
        <div className="text-center space-y-3" data-testid="basar-order-pending">
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
          <h2 className="text-lg font-heading font-semibold">{t("Venter på Vipps")}</h2>
          <p className="text-sm text-muted-foreground">{t("Fullfør betalingen i Vipps-appen – siden oppdateres automatisk. Numrene dine er reservert mens du betaler.")}</p>
          {order.hold_expires_at && <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="basar-order-hold-expires"><Clock className="w-3.5 h-3.5" /> {t("Reservasjonen gjelder til kl. {time}", { time: shortTime(order.hold_expires_at) })}</p>}
        </div>
      );
    }
    if (paid) {
      return (
        <div className="space-y-4" data-testid="basar-order-paid">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"><CheckCircle2 className="w-5 h-5" /></span>
            <div>
              <h2 className="text-lg font-heading font-semibold">{t("Takk for støtten! Loddene er dine.")}</h2>
              <p className="text-sm text-muted-foreground">{order.paid_at ? t("Betalt {date}", { date: formatDateTime(order.paid_at) }) : ""}{order.source === "manual" ? ` · ${t("Registrert av arrangøren")}` : ""}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-2">{numbers.length === 1 ? t("Loddnummeret ditt") : t("Loddnumrene dine ({n})", { n: numbers.length })}</p>
            <div className="flex flex-wrap gap-2" data-testid="basar-order-numbers">
              {numbers.map((n) => (
                <span key={n} className={`inline-flex items-center justify-center min-w-[3.25rem] rounded-lg border px-3 py-2 text-lg font-heading font-semibold tabular-nums ${won.some((w) => w.number === n) ? "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/18 ring-2 ring-[hsl(var(--warning))]/40" : "border-border bg-muted/50"}`} data-testid={`basar-order-number-${n}`}>{n}</span>
              ))}
            </div>
          </div>
          {won.length > 0 && (
            <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4 space-y-1.5" data-testid="basar-order-won">
              <p className="font-heading font-semibold inline-flex items-center gap-2"><Trophy className="w-5 h-5 text-[hsl(var(--warning))]" /> {t("Gratulerer – du har vunnet!")}</p>
              {won.map((w, i) => <p key={i} className="text-sm tabular-nums">{t("Lodd {n}", { n: w.number })}: <span className="font-medium">{w.prize_name}</span>{w.value ? ` (${nok(w.value)})` : ""}</p>)}
              <p className="text-xs text-muted-foreground">{order.basar?.contact_info ? t("Arrangøren tar kontakt om utlevering. Du kan også kontakte arrangøren selv: {contact}", { contact: order.basar.contact_info }) : t("Arrangøren tar kontakt om utlevering.")}</p>
            </div>
          )}
          {/* Kvittering med QR (brukarval 2026-10-28: på skjerm + PDF – arrangøren skannar koden ved utlevering av premiar) */}
          {order.receipt_available !== false && (
            <div className="rounded-xl border border-border bg-muted/30 p-4 flex flex-col sm:flex-row gap-4 sm:items-center" data-testid="basar-order-receipt">
              <div className="shrink-0 self-center sm:self-auto rounded-xl border border-border bg-white p-2 shadow-sm">
                <img
                  src={basarOrderQrUrl(order.id)}
                  alt={t("QR-kode for kvitteringen – vises ved utlevering av premier")}
                  width={148}
                  height={148}
                  className="block h-[148px] w-[148px]"
                  data-testid="basar-order-qr-image"
                />
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <p className="font-heading font-semibold inline-flex items-center gap-2"><QrCode className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Kvittering og utleveringskode")}</p>
                <p className="text-sm text-muted-foreground">{t("Vis denne koden når du henter en premie – arrangøren skanner den i Right in og ser loddene dine og eventuelle gevinster.")}</p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild variant="outline" size="sm" data-testid="basar-order-receipt-pdf-button">
                    <a href={basarOrderReceiptUrl(order.id)} target="_blank" rel="noreferrer"><FileDown className="w-4 h-4 mr-1.5" /> {t("Last ned kvittering (PDF)")}</a>
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2 text-sm">
            <p className="flex items-start gap-2 rounded-lg bg-muted/40 p-3"><Camera className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span className="text-muted-foreground">{t("Ta gjerne et skjermbilde eller lagre denne lenken – den viser alltid loddene dine.")}</span></p>
            <p className="flex items-start gap-2 rounded-lg bg-muted/40 p-3"><Smartphone className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span className="text-muted-foreground">{t("Du finner også loddene igjen under «Mine lodd» på basarsiden med mobilnummeret ditt.")}</span></p>
          </div>
        </div>
      );
    }
    if (order.status === "cancelled") {
      return (
        <div className="space-y-3" data-testid="basar-order-cancelled">
          <div className="flex items-center gap-3"><span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]"><XCircle className="w-5 h-5" /></span><h2 className="text-lg font-heading font-semibold">{t("Kansellert")}</h2></div>
          <p className="text-sm text-muted-foreground">{!order.paid_at ? t("Betalingen ble avbrutt og numrene er frigitt. Du kan velge nummer på nytt.") : t("Bestillingen er kansellert. Er det betalt noe, blir det tilbakebetalt.")}</p>
          <Button asChild variant="outline" size="sm" data-testid="basar-order-retry-link"><Link to={basarUrl(order.basar_id)}><RotateCcw className="w-4 h-4 mr-1.5" /> {t("Velg nummer på nytt")}</Link></Button>
        </div>
      );
    }
    if (order.status === "refunded" || order.status === "refunding") {
      return (
        <div className="space-y-2" data-testid="basar-order-refunded">
          <div className="flex items-center gap-3"><span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-secondary text-muted-foreground"><RotateCcw className="w-5 h-5" /></span><h2 className="text-lg font-heading font-semibold">{t("Refundert")}</h2></div>
          <p className="text-sm text-muted-foreground">{t("Bestillingen er refundert ({amount}) og numrene er frigitt.", { amount: nok(order.refund_amount ?? order.total_amount) })}{order.refunded_at ? ` · ${formatDateTime(order.refunded_at)}` : ""}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-6" data-testid="basar-order-confirmation-page" data-status={order.status}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{t("Loddkjøpet ditt")}</p>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="basar-order-number">{t("Ordre {number}", { number: order.order_number })}</h1>
            <p className="text-sm text-muted-foreground mt-1">{formatDateTime(order.created_at)} · {basarPaymentLabel(order)}</p>
          </div>
          <Badge className={`${st.className} shrink-0 self-start`} data-testid="basar-order-status-badge">{st.label}</Badge>
        </div>

        <Card className="rounded-2xl border-border"><CardContent className="p-6">{statusBlock()}</CardContent></Card>

        <Card className="rounded-2xl border-border" data-testid="basar-order-summary">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="font-heading font-semibold">{t("Basar")}</h2>
                <Link to={basarUrl(order.basar_id)} className="text-sm underline underline-offset-4 hover:text-foreground/80 transition-colors duration-150 inline-flex items-center gap-1.5" data-testid="basar-order-basar-link"><Ticket className="w-4 h-4" /> {order.basar_name}</Link>
                <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {order.organizer_name}</p>
              </div>
            </div>
            {order.basar?.draw_info && !won.length && <p className="text-sm text-muted-foreground inline-flex items-start gap-2" data-testid="basar-order-draw-info"><CalendarClock className="w-4 h-4 mt-0.5 shrink-0" /> {order.basar.draw_info}</p>}
            <div className="rounded-lg border border-border divide-y divide-border text-sm">
              <div className="flex justify-between gap-3 p-3"><span>{numbers.length === 1 ? t("{n} lodd × {price}", { n: 1, price: nok(order.unit_price) }) : t("{n} lodd × {price}", { n: order.quantity, price: nok(order.unit_price) })}</span><span className="tabular-nums">{nok(order.total_amount)}</span></div>
              <div className="flex justify-between gap-3 p-3 font-semibold"><span>{t("Totalt")}</span><span className="tabular-nums" data-testid="basar-order-total">{nok(order.total_amount)}</span></div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 text-sm">
              <p className="flex items-start gap-2"><Smartphone className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span><span className="block font-medium">{order.name}</span>{order.phone_masked}</span></p>
              {order.email && <p className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span className="break-all">{order.email}{paid && emailEnabled ? <span className="block text-xs text-muted-foreground">{t("Kvittering med loddnumrene er sendt på e-post.")}</span> : null}</span></p>}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <p className="text-xs text-muted-foreground" data-testid="basar-order-seller-note">{t("Basaren arrangeres av {org}. Betalingen går direkte til arrangøren – Right in formidler bare kjøpet.", { org: order.organizer_name })}</p>
          <Button asChild variant="ghost" size="sm" data-testid="basar-order-back-link"><Link to={basarUrl(order.basar_id)}><ArrowLeft className="w-4 h-4 mr-1.5" /> {t("Tilbake til basaren")}</Link></Button>
        </div>
      </div>
    </PublicLayout>
  );
}
